/**
 * "Is the screen that submitted this capture still the one on screen" (FR-036's own contract, and
 * `contracts/what-the-practitioner-sees.md`'s "a refused capture that is shown where it was typed
 * must leave the outbox"): a capture refused while its own page is still showing it can dismiss the
 * outbox's row once the reason is on screen (`dismissRefused`); one refused after the practitioner
 * moved on must not surface `problem` on a screen the line was never typed on, and the row stays so
 * the unsent count still tells them.
 *
 * None of the four capture stores (`home`, `container`, `tag`, `review`) reset when their page
 * unmounts — nothing in production calls `resetHome()` and its siblings — so "still mounted" has to
 * come from the page itself, registered here rather than reinvented four times. `register(key)` is
 * called from the page's own mount effect (`useEffect(() => mountGuard.register(key), deps)`) and
 * returns its cleanup; `isCurrent(key)` is what the store's `submitCapture` continuation checks once
 * the server has answered, asynchronously, possibly long after the page that started the capture is
 * gone.
 *
 * Registration is by TOKEN, not by clearing a shared flag on unmount: two effects can overlap for one
 * render (a remount, or React re-running an effect under `StrictMode`), and an older effect's cleanup
 * must never clear a NEWER registration it knows nothing about — only the cleanup closed over the
 * exact token that is still current may clear it.
 */
export interface MountGuard<K> {
  /** Call from the page's own mount effect; returns the cleanup `useEffect` expects. */
  readonly register: (key: K) => () => void;
  /** True while the page identified by `key` (by `sameKey`) is the one currently registered. */
  readonly isCurrent: (key: K) => boolean;
  /** Test seam, like every other module-level store's reset. */
  readonly reset: () => void;
}

export function createMountGuard<K = undefined>(
  sameKey: (a: K, b: K) => boolean = (a, b) => a === b,
): MountGuard<K> {
  let current: { readonly key: K } | null = null;

  function register(key: K): () => void {
    const token = { key };
    current = token;
    return () => {
      if (current === token) current = null;
    };
  }

  function isCurrent(key: K): boolean {
    return current !== null && sameKey(current.key, key);
  }

  function reset(): void {
    current = null;
  }

  return { register, isCurrent, reset };
}
